/**
 * LocationPrompt.js - Modal para solicitar ubicación del usuario
 *
 * Flujo:
 * 1. Muestra opción de usar ubicación actual (GPS)
 * 2. Si rechaza o no soporta, permite seleccionar ciudad manualmente
 */

import React, { useState } from 'react';
import { colors } from '../styles';
import {
  requestBrowserLocation,
  reverseGeocode,
  saveLocation,
  CIUDADES_DISPONIBLES
} from '../services/locationService';

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    backgroundColor: colors.surface,
    borderRadius: '16px',
    width: '100%',
    maxWidth: '400px',
    padding: '1.5rem',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
  icon: {
    fontSize: '2.5rem',
    textAlign: 'center',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: colors.textPrimary,
    textAlign: 'center',
    marginBottom: '0.5rem',
  },
  subtitle: {
    fontSize: '0.875rem',
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: '1.5rem',
  },
  gpsButton: {
    width: '100%',
    padding: '0.875rem 1rem',
    backgroundColor: colors.accent,
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '0.9375rem',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  gpsButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: colors.border,
  },
  dividerText: {
    fontSize: '0.75rem',
    color: colors.textMuted,
  },
  select: {
    width: '100%',
    padding: '0.875rem 1rem',
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: '10px',
    fontSize: '0.9375rem',
    color: colors.textPrimary,
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 1rem center',
  },
  errorMessage: {
    backgroundColor: colors.errorBg,
    border: `1px solid ${colors.error}`,
    borderRadius: '8px',
    padding: '0.75rem',
    marginBottom: '1rem',
    fontSize: '0.8125rem',
    color: colors.error,
    textAlign: 'center',
  },
  loadingSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};

// Agregar keyframes para el spinner
const spinKeyframes = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default function LocationPrompt({ isOpen, onLocationSet, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCitySelect, setShowCitySelect] = useState(false);

  if (!isOpen) return null;

  const handleUseGPS = async () => {
    setLoading(true);
    setError(null);

    try {
      const location = await requestBrowserLocation();

      // Resolver nombre de ubicación con reverse geocoding
      const geo = await reverseGeocode(location.lat, location.lng);
      if (geo.ciudad) location.ciudad = geo.ciudad;
      if (geo.colonia) location.colonia = geo.colonia;

      saveLocation(location);
      onLocationSet(location);
    } catch (err) {
      setError(err.message);
      setShowCitySelect(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCitySelect = (e) => {
    const ciudadNombre = e.target.value;
    if (!ciudadNombre) return;

    const ciudad = CIUDADES_DISPONIBLES.find(c => c.nombre === ciudadNombre);
    if (ciudad) {
      const location = {
        lat: ciudad.lat,
        lng: ciudad.lng,
        ciudad: ciudad.nombre,
        source: 'manual'
      };
      saveLocation(location);
      onLocationSet(location);
    }
  };

  return (
    <>
      <style>{spinKeyframes}</style>
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={e => e.stopPropagation()}>
          <div style={styles.icon}>📍</div>
          <h2 style={styles.title}>¿Dónde estás?</h2>
          <p style={styles.subtitle}>
            Para mostrarte servicios cercanos necesitamos tu ubicación.
          </p>

          {error && (
            <div style={styles.errorMessage}>
              {error}. Selecciona tu ciudad manualmente.
            </div>
          )}

          <button
            style={{
              ...styles.gpsButton,
              ...(loading ? styles.gpsButtonDisabled : {}),
            }}
            onClick={handleUseGPS}
            disabled={loading}
          >
            {loading ? (
              <>
                <div style={styles.loadingSpinner} />
                Obteniendo ubicación...
              </>
            ) : (
              <>
                <span>📍</span>
                Usar mi ubicación actual
              </>
            )}
          </button>

          <div style={styles.divider}>
            <div style={styles.dividerLine} />
            <span style={styles.dividerText}>o selecciona una ciudad</span>
            <div style={styles.dividerLine} />
          </div>

          <select
            style={styles.select}
            onChange={handleCitySelect}
            defaultValue=""
          >
            <option value="" disabled>
              Seleccionar ciudad...
            </option>
            {CIUDADES_DISPONIBLES.map((ciudad) => (
              <option key={ciudad.nombre} value={ciudad.nombre}>
                {ciudad.nombre}
              </option>
            ))}
          </select>
        </div>
      </div>
    </>
  );
}
